/**
 * Helper unificado para notificar erros da API Tools
 *
 * Converte qualquer erro (AppError, APIError, Error) em notificação
 * usando ErrorCatalog e decide automaticamente entre Toast e Modal.
 */

import { APIError } from "./api";
import { AppError, ErrorCode, ErrorSeverity } from "./errors";
import { handleToolsApiError, ToolsErrorContext } from "./toolsErrorHandler";
import { ErrorCatalog } from "../components/feedback/ErrorCatalog";
import { NotificationItem } from "../components/feedback/NotificationProvider";

export interface NotifyToolsErrorOptions {
	/**
	 * Contexto do erro (para melhor detecção)
	 */
	context?: ToolsErrorContext;

	/**
	 * Forçar uso de modal mesmo que preferModal seja false
	 */
	forceModal?: boolean;

	/**
	 * Forçar uso de toast mesmo que preferModal seja true
	 */
	forceToast?: boolean;

	/**
	 * Duração customizada (apenas para toast)
	 */
	duration?: number;

	/**
	 * Ações customizadas adicionais
	 */
	actions?: NotificationItem["actions"];

	/**
	 * Se deve ser sticky (não fecha automaticamente)
	 */
	sticky?: boolean;
}

/**
 * Converte ação do ErrorCatalog em ação de notificação
 */
function convertCatalogActionToNotificationAction(
	catalogAction: { label: string; action: string },
	onRetry?: () => void | Promise<void>,
): NotificationItem["actions"][0] | null {
	if (catalogAction.action === "retry" && onRetry) {
		return {
			label: catalogAction.label,
			onClick: onRetry,
		};
	}

	// Outras ações podem ser implementadas conforme necessário
	// Por enquanto, apenas retry é suportado
	return null;
}

/**
 * Decide se deve usar Modal ou Toast baseado no erro e opções
 */
function shouldUseModal(
	error: AppError,
	catalogEntry: (typeof ErrorCatalog)[ErrorCode] | undefined,
	options?: NotifyToolsErrorOptions,
): boolean {
	// Forçar modal
	if (options?.forceModal) return true;

	// Forçar toast
	if (options?.forceToast) return false;

	// Usar preferModal do catalog se disponível
	if (catalogEntry?.preferModal !== undefined) {
		return catalogEntry.preferModal;
	}

	// Usar severity como fallback
	return error.severity === "critical";
}

/**
 * Notifica erro da API Tools de forma unificada
 *
 * @param error - Erro a ser notificado (AppError, APIError, ou Error genérico)
 * @param notifyFn - Função de notificação do NotificationProvider
 * @param context - Contexto opcional do erro
 * @param options - Opções de notificação
 * @param onRetry - Callback opcional para ação de retry
 * @returns ID da notificação criada
 *
 * @example
 * ```typescript
 * import { useNotifications } from '@/components/feedback/NotificationProvider';
 * import { notifyToolsError } from '@/lib/toolsErrorNotification';
 *
 * const { notify } = useNotifications();
 *
 * try {
 *   await api('/tools/agrupar-objetos-sobrepostos');
 * } catch (error) {
 *   notifyToolsError(error, notify, 'tool', {
 *     onRetry: () => retryRequest()
 *   });
 * }
 * ```
 */
export function notifyToolsError(
	error: AppError | APIError | Error,
	notifyFn: (params: Omit<NotificationItem, "id" | "createdAt">) => string,
	context?: ToolsErrorContext | string,
	options?: NotifyToolsErrorOptions & { onRetry?: () => void | Promise<void> },
): string {
	// Converter para AppError se necessário
	let appError: AppError;

	// ✅ CORRIGIDO: Normalizar contexto (string -> ToolsErrorContext)
	const normalizedContext: ToolsErrorContext | undefined =
		typeof context === "string"
			? (context as ToolsErrorContext)
			: context || options?.context;

	if (error instanceof AppError) {
		appError = error;
	} else if (error instanceof APIError) {
		// Usar handleToolsApiError para converter APIError em AppError
		appError = handleToolsApiError(error, normalizedContext);
	} else {
		// Error genérico - criar AppError básico
		appError = new AppError(error.message || "Ocorreu um erro inesperado", {
			code: ErrorCode.UNKNOWN,
			severity: "error",
		});
	}

	// Buscar entrada no ErrorCatalog
	const catalogEntry = ErrorCatalog[appError.code];

	// Obter mensagem (do catalog ou do erro)
	let message: string;
	if (catalogEntry) {
		message =
			typeof catalogEntry.message === "function"
				? catalogEntry.message(appError.metadata)
				: catalogEntry.message;
	} else {
		message = appError.message;
	}

	// Obter título (do catalog, do erro, ou padrão)
	const title = catalogEntry?.title || appError.title || "Erro";

	// Obter severity (do catalog ou do erro)
	const severity: ErrorSeverity =
		catalogEntry?.severity || appError.severity || "error";

	// Decidir entre Modal e Toast
	const useModal = shouldUseModal(appError, catalogEntry, options);

	// Converter ações do catalog
	const actions: NotificationItem["actions"] = [];

	if (catalogEntry?.actions) {
		for (const catalogAction of catalogEntry.actions) {
			const notificationAction = convertCatalogActionToNotificationAction(
				catalogAction,
				options?.onRetry,
			);
			if (notificationAction) {
				actions.push(notificationAction);
			}
		}
	}

	// Adicionar ações customizadas
	if (options?.actions) {
		actions.push(...options.actions);
	}

	// Determinar tipo de notificação baseado em severity
	const type: NotificationItem["type"] =
		severity === "critical" || severity === "error"
			? "error"
			: severity === "warning"
				? "warning"
				: "info";

	// Duração padrão baseada em tipo e se é modal
	const defaultDuration = useModal
		? undefined
		: type === "error"
			? 10000
			: type === "warning"
				? 8000
				: 5000;

	// Criar notificação
	return notifyFn({
		type,
		code: appError.code,
		title,
		message,
		severity,
		sticky: options?.sticky ?? useModal, // Modais são sticky por padrão
		duration: options?.duration ?? defaultDuration,
		actions: actions.length > 0 ? actions : undefined,
		meta: {
			errorCode: appError.code,
			context: normalizedContext,
			metadata: appError.metadata,
		},
	});
}
